import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { PortalService } from '@vsolv/packages/portal-config/web';
import { Coverage, Policy, Provisioning } from '@wsphere/warranties/domain';
import { BehaviorSubject, combineLatest, map, shareReplay, tap } from 'rxjs';
import { PLAN_PICKER_CONFIG } from '../../config/plan-picker.config';
import { AddonDependencyStatus, cascadeDeselect, evaluateDependencyStatuses } from '../../utils/addon-dependency.utils';
import { formatDurationFromDays } from '../../utils/duration.utils';

interface EvaluatedCoverage {
  coverageId: string;
  coverage?: Coverage.Model;
  requirement: string;
  price: number;
  deductible: number;
  liabilityLimit: number;
  liabilityGroups: string[];
  liabilityGroupObjects?: Policy.Plan.LiabilityGroup[];
}

interface EvaluatedPlan {
  /**
   * The term this evaluation was run against. Optional because older backend deployments (without
   * VS-896's handler change) don't stamp it — `plansAndAddons$` falls back to the policy's first
   * term-id in that case. New backend always populates this.
   *
   * PLAN_FIRST multi-term collections use it to sort and group client-side; TERM_FIRST passes a
   * single-term collection where every entry shares the same `termId`.
   */
  termId?: string;
  planId: string;
  plan: Policy.Plan.Model;
  termDuration?: number;
  coverages: EvaluatedCoverage[];
  eligible: boolean;
  ineligibilityReasons: Provisioning.EvaluatedSessionPlanIneligibilityReason[] | null;
}

export interface EvaluatedAddon {
  addonId: string;
  addon: Policy.Plan.Addon;
  price: number;
  coverages: EvaluatedCoverage[];
}

interface PlanFormValues {
  plan: Policy.Plan.Model | null;
  addonIds: string[] | null;
  termId: string | null;
}

/** Prefixed uuids (`dim_…`, `elem_…`) as minted by VsolvEntity — never fit to show a buyer. */
const RAW_IDENTIFIER_PATTERN = /^[a-z]+_[0-9a-f]{32}$/i;

const GENERIC_INELIGIBILITY_MESSAGE = "Your asset doesn't meet one of this plan's requirements.";

function isRawIdentifier(value: unknown): boolean {
  return typeof value === 'string' && RAW_IDENTIFIER_PATTERN.test(value.trim());
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-plan-picker',
  templateUrl: './plan-picker.component.html',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: PlanPickerComponent,
    },
  ],
})
export class PlanPickerComponent implements ControlValueAccessor {
  constructor(
    private breakpointObserver: BreakpointObserver,
    private portalSvc: PortalService
  ) {}

  readonly config = PLAN_PICKER_CONFIG;

  // Returns the contact email from config, or an empty string if not set.
  // Uses nullish coalescing to provide a safe fallback for maintainability.
  get contactEmail(): string {
    return this.portalSvc.config?.contactEmail ?? '';
  }

  hasAddons = false;

  /**
   * Dependency status map keyed by `${planId}|${termId}` → (addonId → status).
   *
   * Composite key matters in PLAN_FIRST mode where multiple term cards for the same plan render
   * side-by-side: each card has its own `value[termId].addonIds`, so dependency state must be
   * tracked per (plan, term) to avoid toggling an addon on the 6mo card from incorrectly
   * locking/unlocking the same addon on the 12mo card.
   */
  planDependencyStatusMap = new Map<string, Map<string, AddonDependencyStatus>>();

  /** Refresh dependency statuses for a specific (plan, term) given current selection */
  refreshPlanDependencyStatuses(planId: string, termId: string, selectedAddonIds: string[]) {
    const planData = this.evaluatedPlans?.find(p => p.planId === planId);
    const allAddons = planData?.plan?.addons ?? [];
    const statuses = evaluateDependencyStatuses(allAddons, selectedAddonIds);
    this.planDependencyStatusMap.set(
      PlanPickerComponent.priceKey(termId, planId),
      new Map(statuses.map(s => [s.addonId, s]))
    );
  }

  /** Default addons offered for this (plan, term), minus any whose prerequisites aren't also selected. */
  getDefaultAddonIds(planId: string, termId: string): string[] | null {
    const row = this.evaluatedPlans?.find(p => p.planId === planId && (p.termId ?? termId) === termId);
    const addons = row?.plan?.addons ?? [];
    const offeredIds = new Set(row?.coverages.map(coverage => coverage.requirement));
    const defaults = addons.filter(addon => addon.isDefault && offeredIds.has(addon.id)).map(addon => addon.id);
    const addonIds = cascadeDeselect(addons, defaults);
    return addonIds.length ? addonIds : null;
  }

  /** Check if an addon's dependencies are met within a (plan, term) card */
  isDependencyMet(planId: string, termId: string, addonId: string): boolean {
    return (
      this.planDependencyStatusMap.get(PlanPickerComponent.priceKey(termId, planId))?.get(addonId)?.dependenciesMet ??
      true
    );
  }

  /** Get the dependency hint text for an addon in a (plan, term) card */
  getDependencyHint(planId: string, termId: string, addonId: string): string | null {
    const status = this.planDependencyStatusMap.get(PlanPickerComponent.priceKey(termId, planId))?.get(addonId);
    if (!status?.requiredAddonTitles?.length) return null;
    const op = status.operator === 'OR' ? 'any' : 'all';
    return `Requires ${op} of: ${status.requiredAddonTitles.join(', ')}`;
  }

  @PropertyListener('selectedTerm') selectedTerm$ = new BehaviorSubject<Policy.PolicyTerm | undefined>(undefined);
  selectedTerm?: Policy.PolicyTerm;

  isMobile$ = this.breakpointObserver
    .observe([Breakpoints.XSmall, Breakpoints.Small])
    .pipe(map(state => state.matches));

  @Output() buyNow = new EventEmitter();
  @Output() back = new EventEmitter();

  @Output() termChanged = new EventEmitter<string>();

  @Input() viewablePlansAndTerms?: Record<string, string[] | null>;

  @PropertyListener('policy') policy$ = new BehaviorSubject<Policy.Model | null>(null);
  @Input() policy: Policy.Model | null = null;
  @Input() staffView = false;

  /** The term id currently persisted on the session (i.e., the term the parent considers "current"). */
  @Input() sessionTermId: string | null = null;

  @PropertyListener('evaluatedPlans') evaluatedPlans$ = new BehaviorSubject<EvaluatedPlan[]>([]);
  @Input() evaluatedPlans?: EvaluatedPlan[];
  @Input() disabled = false;

  @Input() value: Record<string, PlanFormValues> = {};
  @PropertyListener('value')
  value$ = new BehaviorSubject<Record<string, PlanFormValues>>(this.value);

  @Output() addons = new EventEmitter<{ planId: string; termId: string; addons: EvaluatedAddon[] }[]>();

  /**
   * Admin-dashboard "quote before buying" markup, keyed by `priceKey(termId, planId)`, cents.
   * Owned by `ProvisioningSessionService` one level up — this component stays a dumb emitter so it
   * doesn't have to depend on the `provisioning` feature (which already depends on `policy`).
   */
  @Input() planMarkups: Record<string, number> = {};
  @Input() hideMarkupEntry = false;
  @Output() markupChanged = new EventEmitter<{ termId: string; planId: string; cents: number }>();

  /** Per-policy kill switch (`Policy.allowMarkup`) — mirrors the gate already used at checkout. */
  get markupAllowed(): boolean {
    return Policy.isMarkupAllowed(this.policy);
  }

  getMarkupCents(termId: string, planId: string): number {
    if (!this.markupAllowed) return 0;
    return this.planMarkups?.[PlanPickerComponent.priceKey(termId, planId)] ?? 0;
  }

  getMarkupDollars(termId: string, planId: string): number | null {
    const cents = this.getMarkupCents(termId, planId);
    return cents > 0 ? cents / 100 : null;
  }

  /**
   * Markup is a flat amount added once to the pre-installment subtotal server-side
   * (see preview.handler.ts), then that combined total is split across installments —
   * so each installment's displayed price needs its proportional share, not the full amount.
   */
  installmentTotalWithMarkup(termId: string, planId: string, installment: { total: number; count: number }): number {
    const markupCents = this.getMarkupCents(termId, planId);
    return installment.total + Math.floor(markupCents / (installment.count || 1));
  }

  onMarkupInput(termId: string, planId: string, rawValue: string) {
    if (!this.markupAllowed) return;
    const parsed = parseFloat(rawValue);
    const cents = Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 100) : 0;
    this.markupChanged.emit({ termId, planId, cents });
  }

  touched = false;

  navigation$ = new BehaviorSubject<number>(0);

  CheckoutPlanDisplay = Policy.CheckoutPlanDisplay;

  /**
   * In `PLAN_FIRST` mode, the plan the buyer has selected from the top chip strip.
   * Drives both which detail card is shown and which terms the term-picker exposes.
   */
  selectedPlanId$ = new BehaviorSubject<string | null>(null);

  get planFirst(): boolean {
    return this.policy?.checkoutPlanDisplay === Policy.CheckoutPlanDisplay.PLAN_FIRST;
  }

  /**
   * Build a `viewablePlansAndTerms` filter limited to the plan picked in PLAN_FIRST mode.
   *
   * Returns `undefined` (not `{ [planId]: null }`) when the plan has zero configured terms,
   * because `ws-policy-term-picker` treats a `null` term-list as "all terms allowed" — the
   * opposite of what we want. `undefined` lets the caller fall back explicitly.
   */
  buildPlanFirstTermFilter(planId: string | null): Record<string, string[]> | undefined {
    if (!planId) return undefined;
    const plan = this.policy?.plans?.find(p => p.id === planId);
    if (!plan) return undefined;
    // The API persists a key for every term (with `null`/`[]` value when unconfigured),
    // so we must filter to entries that actually have coverage configs.
    const termIds = Object.entries(plan.coverageConfigs ?? {})
      .filter(([, config]) => Array.isArray(config) && config.length > 0)
      .map(([termId]) => termId);
    if (!termIds.length) return undefined;
    return { [planId]: termIds };
  }

  /**
   * Pre-computed `viewablePlansAndTerms` for PLAN_FIRST mode.
   *
   * Derived as an observable so the template gets a stable reference across change-detection
   * cycles — avoids rebuilding the filter object (and re-triggering the downstream
   * `ws-policy-term-picker` `@Input`) on every CD tick.
   */
  planFirstViewablePlansAndTerms$ = combineLatest([this.policy$, this.selectedPlanId$]).pipe(
    map(([, planId]) => this.buildPlanFirstTermFilter(planId)),
    shareReplay(1)
  );

  /**
   * Plans shown in the PLAN_FIRST chip strip — a **stable** list that does NOT react to the
   * currently-selected term. We surface every policy plan that has at least one configured
   * term (any plan with zero configured terms is unsellable and excluded).
   *
   * The plan detail card and pricing still come from `plansAndAddons$` (which is
   * term-filtered), but the strip itself is term-independent so the customer sees a
   * consistent set of plan options as they switch terms — i.e. a true "pick a plan first,
   * then a term" flow rather than a strip that shrinks/grows with each term selection.
   */
  planFirstPlans$ = this.policy$.pipe(
    map(policy => this.computePlanFirstPlans(policy)),
    shareReplay(1)
  );

  /** Sync helper shared by the observable above and the auto-select tap in `plansAndAddons$`. */
  private computePlanFirstPlans(policy: Policy.Model | null): Policy.Plan.Model[] {
    if (!policy?.plans) return [];
    return policy.plans.filter(plan =>
      Object.values(plan.coverageConfigs ?? {}).some(config => Array.isArray(config) && config.length > 0)
    );
  }

  selectPlanFromStrip(planId: string) {
    this.selectedPlanId$.next(planId);
    // Reset selected term when switching plan so the term-picker auto-selects a valid one.
    if (this.selectedTerm) {
      const filter = this.buildPlanFirstTermFilter(planId);
      const allowed = filter?.[planId];
      if (allowed && !allowed.includes(this.selectedTerm.id)) {
        this.selectedTerm = undefined;
      }
    }
  }

  plansAndAddons$ = combineLatest([this.evaluatedPlans$, this.policy$]).pipe(
    map(([plans, policy]) => {
      if (!plans) return [];
      // Fallback termId for evaluatedPlans entries that don't carry one (older backend that hasn't
      // been redeployed yet). Without this, the composite price-key in `totalPrices$` would key
      // by `undefined|planId` and every template lookup would miss → cards render headers but
      // blank bodies. Falls back to the session's first term — same term the legacy single-call
      // evaluation already implicitly used.
      const fallbackTermId = policy?.terms?.[0]?.id ?? '';
      const planAddons: {
        /** VS-896: PLAN_FIRST renders one card per term — every row now carries its term so the
         *  template can filter / dedupe. TERM_FIRST simply ignores this field (every row shares
         *  the same termId in that mode). */
        termId: string;
        planId: string;
        plan: Policy.Plan.Model;
        basePrice: number;
        coverages: EvaluatedCoverage[];
        addons: EvaluatedAddon[];
        hasBaseCoverages: boolean;
        hasPrice: boolean;
        hasDescription: boolean;
        baseCoveragesCount: number;
        baseGroups: { group: string; coverages: EvaluatedCoverage[]; liabilityGroups: Policy.Plan.LiabilityGroup[] }[];
        /** Hides the "Limit of Liability" column when no base/add-on coverage or its liability group has a limit. */
        hasLiabilityLimit: boolean;
        eligible: boolean;
        ineligibilityReasons: Provisioning.EvaluatedSessionPlanIneligibilityReason[] | null;
      }[] = [];
      const availableAddons: {
        planId: string;
        termId: string;
        addons: EvaluatedAddon[];
      }[] = [];
      let hasPrice = false;
      let hasDescription = false;
      for (const plan of plans) {
        let hasBaseCoverages = false;
        const addons: EvaluatedAddon[] = [];
        let basePrice = 0;
        let baseCoveragesCount = 0;
        const groups: {
          group: string;
          coverages: EvaluatedCoverage[];
          liabilityGroups: Policy.Plan.LiabilityGroup[];
        }[] = [];
        for (const coverage of plan.coverages) {
          if (coverage.requirement === 'NOT_APPLICABLE') continue;
          const price = coverage.price;
          if (!hasPrice && price) hasPrice = true;

          if (coverage.requirement === 'BASE') {
            basePrice += price;
            hasBaseCoverages = true;
            baseCoveragesCount++;
            const coverageGroupName = coverage.coverage?.group || 'Other';
            let coverageGroup = groups.find(group => group.group === coverageGroupName);
            if (!coverageGroup) {
              coverageGroup = { group: coverageGroupName, coverages: [], liabilityGroups: [] };
              groups.push(coverageGroup);
            }

            coverageGroup.coverages.push(coverage);

            (coverage.liabilityGroupObjects ?? []).forEach(liabilityGroup => {
              if (!liabilityGroup) return;
              if (!coverageGroup?.liabilityGroups.some(existing => existing.id === liabilityGroup.id)) {
                coverageGroup?.liabilityGroups.push(liabilityGroup);
              }
            });
            continue;
          }

          if (addons.find(addon => addon.addonId === coverage.requirement)) {
            const index = addons.findIndex(addon => addon.addonId === coverage.requirement);
            addons[index].coverages.push(coverage);
            addons[index].price += price;
          } else {
            const addon = plan.plan.addons?.find(add => add.id === coverage.requirement);
            if (!addon) continue;
            addons.push({
              addonId: addon.id,
              addon,
              price,
              coverages: [coverage],
            });
          }
        }
        if (addons.length) this.hasAddons = true;
        if (!hasDescription && plan.plan.description) hasDescription = true;
        availableAddons.push({ planId: plan.planId, termId: plan.termId ?? fallbackTermId, addons });

        // if (!hasBaseCoverages && !addons.length) continue;

        planAddons.push({
          ...plan,
          // `termId` is already on `plan` (carried over from the input shape) but spread it
          // explicitly so future refactors of the input type don't silently lose it. Fall back
          // to the policy's first term for older backends that don't yet stamp `termId` on each
          // result — keeps composite price keys stable instead of producing `undefined|planId`.
          termId: plan.termId ?? fallbackTermId,
          hasBaseCoverages,
          hasPrice,
          hasDescription,
          basePrice,
          baseCoveragesCount,
          addons,
          baseGroups: groups,
          hasLiabilityLimit: [...groups, ...addons].some(group =>
            group.coverages.some(
              coverage =>
                (coverage.liabilityLimit || 0) > 0 ||
                (coverage.liabilityGroupObjects ?? []).some(liabilityGroup => (liabilityGroup?.limit || 0) > 0)
            )
          ),
          eligible: plan.eligible,
          ineligibilityReasons: plan.ineligibilityReasons,
        });
      }

      planAddons.forEach(item => {
        item.coverages.sort(
          (a, b) =>
            (a.coverage?.order ?? 0) - (b.coverage?.order ?? 0) ||
            (a.coverage?.title ?? '').localeCompare(b.coverage?.title ?? '')
        );

        item.addons.forEach(addon => {
          addon.coverages.sort(
            (a, b) =>
              (a.coverage?.order ?? 0) - (b.coverage?.order ?? 0) ||
              (a.coverage?.title ?? '').localeCompare(b.coverage?.title ?? '')
          );
        });

        item.addons.sort((a, b) => {
          if (a.addonId.startsWith('cov_') && b.addonId.startsWith('cov_')) {
            const aCov = a.coverages[0]?.coverage;
            const bCov = b.coverages[0]?.coverage;
            return (aCov?.order ?? 0) - (bCov?.order ?? 0) || a.addon.title.localeCompare(b.addon.title);
          }

          if (a.addonId.startsWith('cov_')) return 1;
          else if (b.addonId.startsWith('cov_')) return -1;
          else return a.addon.title.localeCompare(b.addon.title);
        });
      });

      this.addons.emit(availableAddons);
      return planAddons;
    }),
    map(plans => plans.filter(plan => !plan.eligible || plan.hasBaseCoverages || plan.addons.length)),
    tap(plans => {
      // Keep loading state until evaluated plans are actually provided by parent.
      if (this.evaluatedPlans == null) {
        this.loading$.next(true);
        return;
      }

      // Initialize dependency statuses per (plan, term) so PLAN_FIRST cards don't share state. In
      // TERM_FIRST mode all rows share the same termId so we only seed one entry per plan; in
      // PLAN_FIRST one entry per term-card gets its own slot.
      for (const plan of plans) {
        const rowTermId = plan.termId || this.selectedTerm?.id || Object.keys(this.value)[0] || '';
        const termValue = this.value[rowTermId];
        const selectedAddonIds = (termValue?.plan?.id === plan.planId ? termValue?.addonIds : null) ?? [];
        this.refreshPlanDependencyStatuses(plan.planId, rowTermId, selectedAddonIds);
      }

      // PLAN_FIRST: default to the first plan from the **stable** chip-strip list (not the
      // term-filtered `plans`) so the strip and the auto-selection stay aligned across term changes.
      if (this.planFirst && !this.selectedPlanId$.value) {
        const stablePlans = this.computePlanFirstPlans(this.policy);
        if (stablePlans.length) {
          this.selectedPlanId$.next(stablePlans[0].id);
        }
      }

      this.loading$.next(false);
    }),
    shareReplay(1)
  );

  loading$ = new BehaviorSubject<boolean>(true);

  /**
   * Composite key used by all price maps in `totalPrices$`. PLAN_FIRST shows multiple terms for the
   * same plan side-by-side, so the legacy `planId`-only key would collide. TERM_FIRST collapses to
   * a single term so the second key segment is effectively constant — both modes share the same
   * lookup shape.
   */
  static priceKey(termId: string, planId: string): string {
    return `${termId}|${planId}`;
  }

  totalPrices$ = combineLatest([this.policy$, this.selectedTerm$, this.value$, this.plansAndAddons$]).pipe(
    map(([policy, selectedTerm, value, plansAndAddons]) => {
      if (!policy) return null;

      const fallbackTerm = policy.terms.find(term => term.id === selectedTerm?.id) ?? policy.terms[0];

      const prices = new Map<
        string,
        { hasOneTimePayment: boolean; price: number; total: number; installments: { total: number; count: number }[] }
      >();
      const addonPrices = new Map<string, { total: number; installments: { total: number; count: number }[] }>();
      const addon = new Map<string, { total: number; installments: { total: number; count: number }[] }>();

      //for every plan
      plansAndAddons.forEach(plan => {
        // Each row is a (plan, term) pair — resolve schedules for the row's own term so PLAN_FIRST
        // can render different installment/one-time pricing per term card simultaneously.
        const term = policy.terms.find(t => t.id === plan.termId) ?? fallbackTerm;
        const termValue: PlanFormValues = value[term.id] ?? { termId: term.id, plan: null, addonIds: null };
        const installmentSchedules = term.paymentSchedules
          .filter(schedule => schedule.installments !== 1)
          .sort((a, b) => b.installments - a.installments);
        const onetimePayment = term.paymentSchedules.some(schedule => schedule.installments === 1);
        const key = PlanPickerComponent.priceKey(term.id, plan.planId);

        let fullPrice = plan.basePrice;
        let addonTotal = 0;
        plan.addons.forEach(addon => {
          //add addon price to total if selected
          if (plan.planId === termValue.plan?.id && termValue.addonIds?.includes(addon.addonId)) {
            fullPrice += addon.price;
            addonTotal += addon.price;
          }

          //calculate addon price to show in table
          addonPrices.set(key + '_' + addon.addonId, {
            total: onetimePayment
              ? addon.price
              : installmentSchedules.length
                ? this.calculatePrice(addon.price, installmentSchedules[0].installments, installmentSchedules[0].rate)
                : 0,
            installments: installmentSchedules.map(schedule => {
              return {
                total: this.calculatePrice(addon.price, schedule.installments, schedule.rate),
                count: schedule.installments,
              };
            }),
          });
        });

        prices.set(key, {
          hasOneTimePayment: onetimePayment,
          price: installmentSchedules.length
            ? this.calculatePrice(fullPrice, installmentSchedules[0].installments, installmentSchedules[0].rate)
            : fullPrice,
          total: onetimePayment
            ? fullPrice
            : installmentSchedules.length
              ? this.calculatePrice(fullPrice, installmentSchedules[0].installments, installmentSchedules[0].rate)
              : 0,
          installments: installmentSchedules.map(schedule => {
            return {
              total: this.calculatePrice(fullPrice, schedule.installments, schedule.rate),
              count: schedule.installments,
            };
          }),
        });

        addon.set(key, {
          total: onetimePayment
            ? addonTotal
            : installmentSchedules.length
              ? this.calculatePrice(addonTotal, installmentSchedules[0].installments, installmentSchedules[0].rate)
              : addonTotal,
          installments: installmentSchedules.map(schedule => {
            return {
              total: this.calculatePrice(addonTotal, schedule.installments, schedule.rate),
              count: schedule.installments,
            };
          }),
        });
      });

      return { totals: prices, addonPrices, addon, addon_hash: Object.fromEntries(addon) };
    })
  );

  /**
   * VS-896 PLAN_FIRST: one card per term the currently-selected plan offers.
   *
   * Filters `plansAndAddons$` (which now spans multiple terms) by the plan picked in the chip
   * strip, then sorts by each term's `order` so cards render left-to-right in the configured
   * policy order. The `term` object is resolved from `policy.terms` so the card template can
   * read `term.duration`, `term.title`, etc. without a second lookup.
   */
  planFirstTermCards$ = combineLatest([this.policy$, this.plansAndAddons$, this.selectedPlanId$]).pipe(
    map(([policy, plansAndAddons, selectedPlanId]) => {
      if (!policy || !selectedPlanId) return [];
      const termById = new Map(policy.terms.map(term => [term.id, term]));
      const cards = plansAndAddons
        .filter(row => row.planId === selectedPlanId)
        .map(row => ({ data: row, term: termById.get(row.termId) }))
        .filter((entry): entry is { data: typeof entry.data; term: Policy.PolicyTerm } => !!entry.term);
      // Sort by the term's configured `order` — locks in the policy's intended sequence regardless
      // of which order the per-term evaluator calls happened to resolve in.
      cards.sort((a, b) => (a.term.order ?? 0) - (b.term.order ?? 0));
      return cards;
    }),
    shareReplay(1)
  );

  /**
   * VS-896 PLAN_FIRST: buy-flow trigger from a term card. Sets the term context first (`selectPlan`
   * requires `this.selectedTerm` to be live) and then emits `buyNow` via `selectPlan(plan, true)`.
   *
   * Doing both in one method ensures the form-state write and the navigation event are coupled —
   * never one without the other — so a user can't end up in checkout with a stale term selection.
   */
  selectPlanFirstCard(plan: Policy.Plan.Model, term: Policy.PolicyTerm) {
    // PLAN_FIRST cards persist full plan state on continue; avoid emitting `termChanged` here
    // because a term-only session update can race and clear addonIds server-side.
    this.ensureTermContext(term);
    this.selectPlan(plan, true);
  }

  /**
   * VS-896 PLAN_FIRST: toggle an addon from within a term card.
   *
   * Each card represents a (plan, term) — but `selectAddon` keys its form-state write by the
   * currently-selected term. We re-point `selectedTerm` to this card's term first so the addon
   * is written under the correct termId. `@PropertyListener` keeps `selectedTerm$` in sync so
   * downstream observables (`totalPrices$`) see the new context immediately.
   */
  selectAddonForTermCard(plan: Policy.Plan.Model, term: Policy.PolicyTerm, addonId: string) {
    this.ensureTermContext(term);
    this.selectAddon(plan, addonId);
  }

  /** Pair with `selectAddonForTermCard` for deselection. */
  deselectAddonForTermCard(term: Policy.PolicyTerm, addonId: string) {
    this.ensureTermContext(term);
    this.deselectAddon(addonId);
  }

  /**
   * Pin `selectedTerm` to the given term so subsequent addon ops write to the right form-state
   * slot. Initialises `value[termId]` if absent so `selectAddon` sees a non-empty entry on the
   * first toggle.
   */
  private ensureTermContext(term: Policy.PolicyTerm) {
    if (this.selectedTerm?.id !== term.id) {
      this.selectedTerm = term;
    }
    if (!this.value[term.id]) {
      this.value = { ...this.value, [term.id]: { termId: term.id, plan: null, addonIds: null } };
    }
  }

  private _onChange?: (value: PlanFormValues) => void;
  private _onTouched?: () => void;

  selectTerm(termId: string) {
    // Re-entering the Plan route auto-selects the persisted term after the evaluated plans have
    // already loaded. Do not put the picker back into a loading state when that selection cannot
    // trigger another evaluation; otherwise the parent deliberately skips the no-op session update
    // and nothing remains to clear the loader.
    if (termId !== this.sessionTermId) this.loading$.next(true);
    let termValue = this.value[termId];
    if (termValue) {
      this.selectedTerm = this.policy?.terms.find(term => term.id === termId);

      this.markAsTouched();
      this._onChange?.(termValue);
      this.termChanged.emit(termId);

      return;
    }

    this.markAsTouched();
    termValue = { termId, plan: null, addonIds: null };
    this.value = { ...this.value, [termId]: termValue };
    this._onChange?.(termValue);

    this.selectedTerm = this.policy?.terms.find(term => term.id === termId);

    this.termChanged.emit(termId);
  }

  selectPlan(plan: Policy.Plan.Model, emitEvent = false) {
    if (!this.selectedTerm) {
      return;
    }
    const termId = this.selectedTerm.id;
    let termValue = this.value[termId];

    // Defaults apply only on a new (plan, term) selection, so a customer's deselection is kept.
    if (!termValue) {
      termValue = { termId, plan, addonIds: this.getDefaultAddonIds(plan.id, termId) };
      this.value = { ...this.value, [termId]: termValue };
      this.markAsTouched();
      this.refreshPlanDependencyStatuses(plan.id, termId, termValue.addonIds ?? []);
      this._onChange?.(termValue);
    }

    if (termValue.plan?.id !== plan.id) {
      this.markAsTouched();
      termValue = { termId: termValue.termId, addonIds: this.getDefaultAddonIds(plan.id, termId), plan };
      this.value = { ...this.value, [termId]: termValue };
      this.refreshPlanDependencyStatuses(plan.id, termId, termValue.addonIds ?? []);
      this._onChange?.(termValue);
    }

    // Buy Now must always resync the outer form control to *this* term's value, even when
    // `value[termId]` already existed and didn't change (e.g. re-clicking Buy Now on a term
    // visited before). Otherwise the control keeps whatever term was last edited elsewhere
    // (e.g. addons picked on a different term card), and the addon step reads that stale term.
    if (emitEvent && termValue.plan) {
      this._onChange?.(termValue);
      this.buyNow.emit();
    }
  }

  hasSelectedAddonCoverage(planId: string, termId: string, addons: EvaluatedAddon[]): boolean {
    const selected = this.value[termId];
    return (
      selected?.plan?.id === planId &&
      addons.some(addon => selected.addonIds?.includes(addon.addonId) && addon.coverages.length > 0)
    );
  }

  selectAddon(plan: Policy.Plan.Model, addonId: string) {
    if (!this.selectedTerm) {
      return;
    }

    const termId = this.selectedTerm.id;
    let termValue = this.value[termId];

    if (!termValue) {
      const defaults = this.getDefaultAddonIds(plan.id, termId) ?? [];
      termValue = { termId, plan, addonIds: defaults.includes(addonId) ? defaults : [...defaults, addonId] };
      this.value = { ...this.value, [termId]: termValue };
      this.markAsTouched();
      this.refreshPlanDependencyStatuses(plan.id, termId, termValue.addonIds ?? []);
      this._onChange?.(termValue);
      return;
    }

    if (termValue.plan?.id === plan.id && termValue.addonIds?.includes(addonId)) return; //nothing to update
    this.markAsTouched();
    let addonIds: string[] = [];

    if (termValue.plan?.id === plan.id) {
      addonIds = [...(termValue.addonIds || [])];
    } else {
      // Keep the new plan's defaults set by `selectPlan`.
      this.selectPlan(plan, false);
      addonIds = [...(this.value[termId]?.addonIds ?? [])];
    }
    if (!addonIds.includes(addonId)) addonIds = [...addonIds, addonId];
    termValue = { plan, addonIds, termId };

    this.value = { ...this.value, [termId]: termValue };
    this.refreshPlanDependencyStatuses(plan.id, termId, addonIds);
    this._onChange?.(termValue);
  }

  deselectAddon(addonId: string) {
    if (!this.selectedTerm) {
      return;
    }
    const termId = this.selectedTerm.id;
    let termValue = this.value[termId];

    if (termValue.addonIds?.includes(addonId)) {
      this.markAsTouched();
      const withoutTarget = termValue.addonIds.filter(id => id !== addonId);

      // Cascade: remove any addons whose dependencies are no longer met
      const plan = termValue.plan;
      const allAddons = plan?.addons ?? [];
      const cascaded = cascadeDeselect(allAddons, withoutTarget);

      termValue = { ...termValue, addonIds: cascaded.length ? cascaded : null };
      this.value = { ...this.value, [termId]: termValue };
      this.refreshPlanDependencyStatuses(plan?.id ?? '', termId, cascaded);
      this._onChange?.(termValue);
    }
  }

  writeValue(value: PlanFormValues | null): void {
    if (!value?.termId) return;

    this.value = { [value.termId]: value };
    if (!this.selectedTerm) this.selectTerm(value.termId);
  }

  registerOnChange(onChange: (value: PlanFormValues) => void): void {
    this._onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this._onTouched = onTouched;
  }

  formatTermDuration(duration?: number): string {
    return formatDurationFromDays(duration);
  }

  markAsTouched() {
    if (!this.touched) {
      this._onTouched?.();
      this.touched = true;
    }
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  getEvaluatedCoverage(coverageId: string, coverages: EvaluatedCoverage[]) {
    return coverages?.find(cov => cov.coverageId === coverageId);
  }

  getEvaluatedLiabilityGroupInfo(
    planLiabilityGroups: Policy.Plan.LiabilityGroup[] | null,
    coverageLiabilityGroups: string[]
  ) {
    return planLiabilityGroups?.filter(group => coverageLiabilityGroups.indexOf(group.id) !== -1);
  }

  calculatePrice(amount: number, installments: number, rate: number) {
    let rateIncrease = 0;
    if (rate !== 0) {
      rateIncrease = (amount * rate) / 100;
    }
    return Math.floor((amount + rateIncrease) / installments);
  }

  goBack() {
    this.back.emit();
  }

  formatIneligibilityReason(reason: Provisioning.EvaluatedSessionPlanIneligibilityReason): string {
    if (this.hasUnresolvedIdentifier(reason)) return GENERIC_INELIGIBILITY_MESSAGE;

    const rawPropertyLabel = String(reason.propertyLabel ?? '')
      .trim()
      .toLowerCase();
    const rawPropertyId = String(reason.propertyId ?? '')
      .trim()
      .toLowerCase();
    const isGenericAsset = rawPropertyLabel === 'asset' || rawPropertyId === 'asset';
    const hasPropertyContext = !!(reason.propertyLabel || reason.propertyId) && !isGenericAsset;
    const contextualPropertyLabel = hasPropertyContext ? this.getDisplayPropertyLabel(reason) : null;
    const actualValue =
      reason.actualValue !== null && reason.actualValue !== undefined
        ? this.formatReasonValue(reason.actualValue)
        : null;

    switch (reason.code) {
      case 'rating_matrix_asset_classified_excluded':
        if (contextualPropertyLabel && actualValue) {
          return `This plan doesn't cover the selected ${contextualPropertyLabel}: ${actualValue}.`;
        }
        if (contextualPropertyLabel) {
          return `This plan doesn't cover the selected ${contextualPropertyLabel}.`;
        }
        return "This type of asset isn't covered by this plan.";
      case 'rating_matrix_asset_not_configured':
        if (contextualPropertyLabel && actualValue) {
          return `This plan isn't available for the selected ${contextualPropertyLabel}: ${actualValue}.`;
        }
        if (contextualPropertyLabel) {
          return `This plan isn't available for the selected ${contextualPropertyLabel}.`;
        }
        return "This plan doesn't support this type of asset.";
      case 'rating_matrix_asset_not_eligible':
        if (contextualPropertyLabel && actualValue) {
          return `This plan isn't available for the selected ${contextualPropertyLabel}: ${actualValue}.`;
        }
        return "This type of asset isn't eligible for this plan.";
      default:
        break;
    }

    if (!reason.operator || reason.expectedValue === null || reason.expectedValue === undefined) {
      return reason.message;
    }

    const propertyLabel = this.getDisplayPropertyLabel(reason);
    const expected = this.formatReasonValue(reason.expectedValue);

    switch (reason.operator) {
      case '<':
        return `${propertyLabel} must be less than ${expected}.`;
      case '<=':
        return `${propertyLabel} must be ${expected} or less.`;
      case '>':
        return `${propertyLabel} must be greater than ${expected}.`;
      case '>=':
        return `${propertyLabel} must be ${expected} or more.`;
      case '=':
        return `${propertyLabel} must be ${expected}.`;
      case '!=':
        return `${propertyLabel} must not be ${expected}.`;
      case 'contains':
        return `${propertyLabel} must contain ${expected}.`;
      case 'does not contain':
        return `${propertyLabel} must not contain ${expected}.`;
      case 'starts with':
        return `${propertyLabel} must start with ${expected}.`;
      case 'does not start with':
        return `${propertyLabel} must not start with ${expected}.`;
      case 'ends with':
        return `${propertyLabel} must end with ${expected}.`;
      case 'does not end with':
        return `${propertyLabel} must not end with ${expected}.`;
      case 'in':
        return `${propertyLabel} must be one of ${expected}.`;
      case 'not in':
        return `${propertyLabel} must not be one of ${expected}.`;
      case 'is empty':
        return `${propertyLabel} must be empty.`;
      case 'is not empty':
        return `${propertyLabel} must be provided.`;
      case 'matches configured range':
        return `${propertyLabel} must match a configured rating range (${expected}).`;
      case 'matches configured option':
        return `${propertyLabel} must match a configured rating option (${expected}).`;
      default:
        return `${propertyLabel} must satisfy ${reason.operator} ${expected}.`;
    }
  }

  formatIneligibilityActualValue(reason: Provisioning.EvaluatedSessionPlanIneligibilityReason): string | null {
    if (this.hasUnresolvedIdentifier(reason)) return null;

    switch (reason.code) {
      case 'rating_matrix_asset_classified_excluded':
      case 'rating_matrix_asset_not_configured':
      case 'rating_matrix_asset_not_eligible':
        return null;
      default:
        break;
    }

    if (reason.actualValue === null || reason.actualValue === undefined) return null;
    const propertyLabel = this.getDisplayPropertyLabel(reason).toLowerCase();
    return `Your ${propertyLabel}: ${this.formatReasonValue(reason.actualValue)}.`;
  }

  /**
   * True when a prefixed uuid the API couldn't resolve reached us (e.g. a hard-deleted dimension).
   * Ids mean nothing to a buyer, so the whole reason falls back to a generic sentence.
   */
  private hasUnresolvedIdentifier(reason: Provisioning.EvaluatedSessionPlanIneligibilityReason): boolean {
    const label = String(reason.propertyLabel ?? reason.propertyId ?? '').trim();
    const leaf = label.includes('.') ? label.slice(label.lastIndexOf('.') + 1) : label;
    if (isRawIdentifier(leaf)) return true;

    return [reason.expectedValue, reason.actualValue].some(value =>
      (Array.isArray(value) ? value : [value]).some(entry => isRawIdentifier(entry))
    );
  }

  private getDisplayPropertyLabel(reason: Provisioning.EvaluatedSessionPlanIneligibilityReason): string {
    const rawLabel = (reason.propertyLabel || reason.propertyId || 'This plan').trim();
    if (!rawLabel) return 'This plan';

    if (!rawLabel.includes('.')) {
      return this.humanizeIdentifier(rawLabel);
    }

    const pathParts = rawLabel.split('.').filter(Boolean);
    const root = pathParts[0] ?? rawLabel;
    const leaf = pathParts[pathParts.length - 1] ?? rawLabel;
    const lowerRoot = root.toLowerCase();
    const lowerLeaf = leaf.toLowerCase();
    const strippedLeaf = lowerLeaf.startsWith(lowerRoot) ? leaf.slice(root.length) : leaf;
    const leafLabel = this.humanizeIdentifier(strippedLeaf || leaf);

    if (lowerRoot.includes('vehicle')) {
      return `Vehicle ${leafLabel.toLowerCase()}`;
    }

    return leafLabel;
  }

  private humanizeIdentifier(value: string): string {
    return value
      .replace(/[_-]+/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^./, first => first.toUpperCase());
  }

  private formatReasonValue(value: Provisioning.EvaluatedSessionPlanReasonValue): string {
    if (Array.isArray(value)) {
      return value.map(entry => this.formatScalarReasonValue(entry)).join(', ');
    }

    return this.formatScalarReasonValue(value);
  }

  private formatScalarReasonValue(value: string | number | boolean | Date | null): string {
    if (value === null || value === undefined) return 'n/a';
    if (value instanceof Date) return value.toLocaleDateString();
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  }

  /**
   * Clamp the carousel index into `[0, length - 1]`. `navigation$` is a global subject that persists
   * across plan-strip clicks — without this, switching from a 3-term plan at index 2 to a 1-term
   * plan would dereference `termCards[2]` on a length-1 array and throw a template error.
   *
   * Treats nullish/negative values as `0` so the first card always renders even before the user
   * has interacted with the carousel arrows.
   */
  clampCarouselIndex(current: number | null | undefined, length: number): number {
    if (!length) return 0;
    const idx = current ?? 0;
    if (idx < 0) return 0;
    if (idx >= length) return length - 1;
    return idx;
  }
}
